import React, { useEffect, useRef, useState } from 'react';
import { uploadsAPI } from '../services/api';

let pdfjsLib = null;
const thumbCache = new Map(); // key -> dataURL
const inflight = new Map();   // key -> Promise<string>

const loadScript = (src) =>
  new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = src;
    el.async = true;
    el.onload = resolve;
    el.onerror = reject;
    document.head.appendChild(el);
  });

const ensurePdfJs = async () => {
  if (pdfjsLib) return pdfjsLib;
  // Try to use global if already present
  if (typeof window !== 'undefined' && window.pdfjsLib) {
    pdfjsLib = window.pdfjsLib;
  } else {
    // Load core from CDN (no bundler dependency)
    const version = '3.11.174';
    await loadScript(`https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.min.js`);
    pdfjsLib = window.pdfjsLib;
  }
  // Configure worker from CDN
  try {
    const version = pdfjsLib.version || '3.11.174';
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.min.js`;
  } catch {
    pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  }
  return pdfjsLib;
};

const PdfThumbnail = ({ fileId, url, thumbnailUrl, filename, blob, sizeClass = 'w-20 h-20', onClick }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [thumbSrc, setThumbSrc] = useState('');
  const [embedSrc, setEmbedSrc] = useState('');
  const embedUrlRef = useRef('');
  const sizeKey = sizeClass ? `|size:${sizeClass}` : '';
  const cacheKey = fileId ? `id:${fileId}${sizeKey}` : (url ? `url:${url}${sizeKey}` : '');

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    // Initialize size
    const update = () => {
      const w = el.clientWidth || 0;
      const h = el.clientHeight || 0;
      setBox(prev => (prev.w !== w || prev.h !== h ? { w, h } : prev));
    };
    update();
    // Observe size changes for hidden-to-visible transitions
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
    } else {
      const id = setInterval(update, 250);
      ro = { disconnect: () => clearInterval(id) };
    }
    // Also update after layout tick
    const raf = requestAnimationFrame(update);
    return () => {
      if (ro && ro.disconnect) ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);

  useEffect(() => {
    if (thumbnailUrl) {
      setThumbSrc(thumbnailUrl);
      setLoading(false);
      setError('');
      return;
    }
    // If cached image exists, use immediately
    if (cacheKey && thumbCache.has(cacheKey)) {
      setThumbSrc(thumbCache.get(cacheKey));
      setLoading(false);
      setError('');
      return;
    }
    // Attach to in-flight render if any
    if (cacheKey && inflight.has(cacheKey)) {
      setLoading(true);
      inflight.get(cacheKey)
        .then((src) => { if (src) setThumbSrc(src); setLoading(false); })
        .catch(() => { setError('Failed to render PDF'); setLoading(false); });
      return;
    }
    let cancelled = false;
    let objectUrl = '';
    async function render() {
      try {
        setLoading(true);
        setError('');
        const lib = await ensurePdfJs();
        // Compute target box size; do not depend solely on layout to avoid blanks
        const parseDesiredPx = (cls, prefix, fallback) => {
          try {
            const tokens = String(cls || '').split(/\s+/);
            const token = tokens.find(t => t.startsWith(prefix));
            if (!token) return fallback;
            // bracket syntax: w-[80px]
            if (token.startsWith(`${prefix}[`)) {
              const m = token.match(/\[(\d+)px\]/);
              if (m) return parseInt(m[1], 10) || fallback;
            }
            // numeric scale: w-20 -> ~80px (n * 4px)
            const m2 = token.match(new RegExp(`^${prefix}(\\d+)$`));
            if (m2) return (parseInt(m2[1], 10) || 0) * 4 || fallback;
          } catch {}
          return fallback;
        };
        const desiredW = (containerRef.current?.clientWidth || 0) || parseDesiredPx(sizeClass, 'w-', 80);
        const desiredH = (containerRef.current?.clientHeight || 0) || parseDesiredPx(sizeClass, 'h-', desiredW);
        // Resolve a Blob to render
        let pdfBlob = null;
        if (blob instanceof Blob) {
          pdfBlob = blob;
        } else if (fileId) {
          const res = await uploadsAPI.fetchBlob(fileId);
          const mime = res?.headers?.['content-type'] || '';
          pdfBlob = res?.data instanceof Blob ? res.data : new Blob([res.data], { type: mime || 'application/pdf' });
        } else if (url) {
          // Best effort fallback (may fail if server requires auth)
          const res = await fetch(url);
          const bufFetched = await res.arrayBuffer();
          pdfBlob = new Blob([bufFetched], { type: res.headers.get('content-type') || 'application/pdf' });
        } else {
          throw new Error('No source for PDF');
        }
        const buf = await pdfBlob.arrayBuffer();
        // Render without worker to avoid CSP/network issues with CDN worker
        const pdf = await lib.getDocument({ data: buf, disableWorker: true }).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1.0 });
        const canvas = canvasRef.current;
        if (!canvas) return;
        // Fit into actual box size to avoid tailwind-unit parsing issues
        const boxW = desiredW;
        const boxH = desiredH;
        const dpr = Math.max(1, window.devicePixelRatio || 1);
        const cssScale = Math.max(0.1, Math.min(boxW / viewport.width, boxH / viewport.height));
        const scaledViewport = page.getViewport({ scale: cssScale * dpr });
        const ctx = canvas.getContext('2d');
        // Canvas pixel size considers DPR, but CSS size uses CSS pixels (boxW/boxH)
        canvas.width = Math.max(1, Math.floor(scaledViewport.width));
        canvas.height = Math.max(1, Math.floor(scaledViewport.height));
        // Ensure canvas displays within the container box
        canvas.style.width = `${Math.floor(viewport.width * cssScale)}px`;
        canvas.style.height = `${Math.floor(viewport.height * cssScale)}px`;
        await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;
        if (cancelled) return;
        // Create object URL for potential reuse (iframe fallback)
        objectUrl = URL.createObjectURL(pdfBlob);
        // Check if canvas actually has content; if blank, prefer embed fallback
        try {
          const sample = canvas.getContext('2d').getImageData(0, 0, Math.min(10, canvas.width), Math.min(10, canvas.height)).data;
          const sum = sample ? sample.reduce((s, v) => s + v, 0) : 0;
          if (!sum) {
            setEmbedSrc(objectUrl);
            embedUrlRef.current = objectUrl;
            setThumbSrc('');
            return;
          }
        } catch {
          // In case of tainted canvas or other issues, fallback to embed
          setEmbedSrc(objectUrl);
          embedUrlRef.current = objectUrl;
          setThumbSrc('');
          return;
        }
        // Convert to dataURL and cache so subsequent renders don't re-run pdf.js
        const dataUrl = canvas.toDataURL('image/png');
        if (cacheKey) thumbCache.set(cacheKey, dataUrl);
        setThumbSrc(dataUrl);
      } catch (e) {
        if (!cancelled) setError('Failed to render PDF');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (cacheKey) {
      const p = render()
        .then(() => thumbCache.get(cacheKey) || '')
        .catch(() => '');
      inflight.set(cacheKey, p);
      p.finally(() => inflight.delete(cacheKey));
    } else {
      render();
    }
    return () => {
      cancelled = true;
      // Do not revoke if it's the actively used embed URL
      if (objectUrl && objectUrl !== embedUrlRef.current) {
        try { URL.revokeObjectURL(objectUrl); } catch {}
      }
    };
  }, [fileId, url, box.w, box.h, thumbnailUrl, sizeClass, cacheKey]);

  // Revoke previous embed URL when replaced
  useEffect(() => {
    const prev = embedUrlRef.current;
    embedUrlRef.current = embedSrc || '';
    return () => {
      if (prev && prev !== embedUrlRef.current) {
        try { URL.revokeObjectURL(prev); } catch {}
      }
    };
  }, [embedSrc]);

  return (
    <div
      ref={containerRef}
      className={`${sizeClass} bg-gray-100 border rounded overflow-hidden shadow flex items-center justify-center cursor-pointer`}
      onClick={onClick}
      title={filename || 'PDF'}
    >
      {loading ? (
        <div className="text-xs text-gray-500">Loading…</div>
      ) : error ? (
        <div className="text-[10px] font-medium text-gray-600">PDF</div>
      ) : thumbSrc ? (
        <img src={thumbSrc} alt={filename || 'PDF'} className="w-full h-full object-contain" />
      ) : embedSrc ? (
        <object data={embedSrc} type="application/pdf" className="w-full h-full" aria-label={filename || 'PDF'} />
      ) : (
        <canvas ref={canvasRef} className="max-w-full max-h-full" />
      )}
    </div>
  );
};

export default PdfThumbnail;


